"use client";

import React, { Dispatch, FC, ReactNode, createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";

import { loadData } from "@/components/AvailableUnitsList/action";
import { LoadingContext } from "@/context/LoaderContext";
import { CommercetoolsUnit, Facet, FacetValue, Pagination } from "@/types";

import { UserContext } from "./UserContext";

export const pageSize = 10;

export interface Params {
  locale?: string;
  category?: string;
  bedrooms?: number;
  floor?: number;
  planType?: string;
  unitViews?: string;
  limit?: number;
  offset?: number;
  withFilters?: boolean;
  unitName?: string;
}
type ParamsAction = {
  update: "UPDATE";
  payload: Params;
};
const allowedSearchParams = ["locale", "category", "bedrooms", "floor", "planType", "unitViews", "limit", "offset", "withFilters", "unitName"];
const visualFilters = ["planType", "unitViews", "floor"];

interface AvailableUnitProps {
  filteredResults: CommercetoolsUnit[];
  filters: Facet[];
  total: number;
  pagination: Pagination;
  dispatch: Dispatch<ParamsAction>;
  setPage: Dispatch<number>;
  params: Params;
  propertyType?: string;
  projectUrl?: string;
  filterResultLoading?: boolean;
}
export const AvailableUnitContext = createContext<AvailableUnitProps>({
  filteredResults: [],
  filters: [],
  total: 0,
  pagination: { total: 1, current: 1, size: 10 },
  dispatch: () => {},
  setPage: () => {},
  params: {},
  filterResultLoading: false,
});

interface Props {
  children: ReactNode;
  results?: CommercetoolsUnit[];
  facets?: Facet[];
  total?: number;
  category?: string;
  bedrooms?: number;
  locale: string;
  unitName?: string;
  propertyType?: string;
  projectUrl?: string;
  serverSideCookieCurrency?: string;
  currencyFromApiRes?: string;
  isBelongToCommunity?: any;
  matchedCommunity?: any;
  filterResultLoading?: boolean;
}
function paramsReducer(currentState: Params, action: ParamsAction): Params {
  const key = Object.keys(action.payload);
  if (key.filter((x) => allowedSearchParams.includes(x)).length > 0) {
    return { ...currentState, ...action.payload };
  } else {
    return currentState;
  }
}

function mergeFilters(baseFilters: Facet[], newFilters: Facet[]): Facet[] {
  const allowedFilters = baseFilters.filter((facet: Facet) => {
    const key = Object.keys(facet)[0];
    return visualFilters.includes(key);
  });
  const mapped: Facet[] = allowedFilters.reduce((final: Facet[], current: Facet) => {
    const key = Object.keys(current)[0];
    const existingFacet = newFilters.find((filter) => Object.keys(filter)[0] === key);
    const newValue = current[key].map((filter: FacetValue) => {
      const existing = existingFacet?.[key].find((x: FacetValue) => x.value === filter.value);
      return { ...filter, enabled: !!existing };
    });
    final.push({ [key]: newValue });
    return final;
  }, []);
  return mapped;
}

const AvailableUnitListProvider: FC<Props> = ({
  children,
  results,
  facets,
  total = 0,
  category,
  bedrooms,
  locale,
  unitName,
  propertyType,
  projectUrl,
  serverSideCookieCurrency,
  currencyFromApiRes,
  isBelongToCommunity,
  matchedCommunity,
}) => {
  // const baseFilters: Facet[] = useMemo(() => {
  //   return facets || [];
  // }, [facets]);
  const [filters, setFilters] = useState<Facet[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ total: Math.ceil(total / pageSize), current: 1, size: pageSize });
  const [filteredResults, setFilteredResults] = useState<CommercetoolsUnit[]>(results || []);
  const [totalResults, setTotalResults] = useState<number>(total);
  const [filterResultLoading, setfilterResultLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const { currency, measurement } = useContext(UserContext);
  const { loading } = useContext(LoadingContext);

  const [params, dispatch] = useReducer(paramsReducer, {
    locale: locale,
    category: category,
    ...(!!bedrooms && Object.keys(bedrooms).length > 0 ? { bedrooms: bedrooms } : undefined),
    offset: 0,
    limit: pageSize,
    withFilters: true,
    unitName,
  });

  // get the results when params update (if category is valorized)
  useEffect(() => {
    if (!category) {
      setTotalResults(0);
      setFilteredResults([]);
      setFilters([]);
      setPagination({ total: 0, current: 0, size: 0 });
      setfilterResultLoading(false);
    } else {
      const newParams = { ...params, currency: currency, measurement: measurement };
      // console.info("category id: ", params.category);
      setfilterResultLoading(true);
      loadData(newParams).then((response) => {
        // console.info("response for category id: ", params.category, response);
        setTotalResults(response.total);
        setFilteredResults(response.results);
        setFilters(response.filters);
        setPagination({
          ...pagination,
          total: Math.ceil(response.total / pageSize),
          current: params.offset === 0 ? 1 : pagination.current,
        });
        loading(false);
        setfilterResultLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, currency, measurement]);

  // update the results and pagination when page changes
  useEffect(() => {
    if (page != pagination.current) {
      setPagination({
        ...pagination,
        current: page,
      });
      dispatch({
        update: "UPDATE",
        payload: { offset: (page - 1) * pageSize },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <AvailableUnitContext.Provider
      value={{
        total: totalResults,
        filteredResults,
        pagination,
        dispatch,
        setPage,
        filters,
        params,
        propertyType,
        projectUrl,
        filterResultLoading,
      }}
    >
      {children}
    </AvailableUnitContext.Provider>
  );
};
export default AvailableUnitListProvider;
